export interface Product {
  slug: string;
  name: string;
  tagline: string;
  subtitle: string;
  description: string;
  longDescription: string;
  productType: 'digital-download' | 'course' | 'template';
  currentPrice: number;
  futurePrice: number;
  currency: 'GBP';
  stripePriceIdEnv: string;
  futurePriceIdEnv?: string;
  image: string;
  status: 'active' | 'coming-soon' | 'sold-out';
  includedResources: string[];
  features: string[];
  whatYouGet: {
    title: string;
    description: string;
  }[];
  isFoundingPriceActive: boolean;
  downloadFiles: {
    name: string;
    description: string;
    filename: string;
  }[];
}

export const products: Product[] = [
  {
    slug: 'unhinged-guide',
    name: 'The Unhinged Guide to Breaking Into Tech',
    tagline: 'The exact strategy I would use if I had to start again today',
    subtitle: 'The exact strategy I would use if I had to start again today, including the systems, templates, AI prompts and tactics I would use to actually land the job.',
    description: 'A complete, practical career-switching system based on my transition from maths teaching into software engineering, six years of professional experience, and what I know now that I didn\'t know when I started.',
    longDescription: `This is not a cheap ebook. This is a complete, practical career-switching system.

After six years as a software engineer — including working with GCHQ, Goldman Sachs, Monzo, and Beamery — I know what actually works when breaking into tech.

More importantly, I know what I would do differently if I had to start again today.

This guide contains the exact strategy I would use in 2026, including:

• How AI has fundamentally changed learning, portfolio building, applications, networking, and interviews
• The realities of entering technology now (not five years ago)
• Practical systems that help you execute rather than endlessly consume content
• Real templates, prompts, and frameworks you can use immediately

This is based on my own transition from teaching mathematics to software engineering, the lessons learned from six years in the industry, and what actually works in today's market.`,
    productType: 'digital-download',
    currentPrice: 39,
    futurePrice: 59,
    currency: 'GBP',
    stripePriceIdEnv: 'REACT_APP_STRIPE_PRICE_ID_UNHINGED_GUIDE',
    futurePriceIdEnv: 'REACT_APP_STRIPE_PRICE_ID_UNHINGED_GUIDE_FUTURE',
    image: '/unhinged-toolkit/unhinged-01-product-card copy.jpg',
    status: 'active',
    includedResources: [
      'The complete Unhinged Guide to Breaking Into Tech',
      'A separate 22-part execution toolkit',
      'The consolidated AI prompt library',
      'CV, networking and interview templates',
      'The 30-day break into tech action plan',
    ],
    features: [
      'Complete career-switching roadmap from teaching to tech',
      'AI-powered learning and portfolio building strategies for 2026',
      'Real networking templates that actually work',
      'Interview preparation frameworks from someone who\'s been on both sides',
      'The exact systems I used to land roles at top companies',
      'Honest advice about what works (and what doesn\'t) in today\'s market',
      'Practical execution toolkit to implement immediately',
      'AI prompt library for accelerated learning',
    ],
    whatYouGet: [
      {
        title: 'The Complete Guide',
        description: 'The full Unhinged Guide to Breaking Into Tech covering the complete career-switching strategy, learning path, portfolio building, networking, and interview preparation.',
      },
      {
        title: '22-Part Execution Toolkit',
        description: 'Separate downloadable toolkit with templates, checklists, and frameworks you can use immediately to execute the strategy.',
      },
      {
        title: 'AI Prompt Library',
        description: 'Consolidated library of AI prompts for learning, coding practice, portfolio projects, CV optimization, and interview preparation.',
      },
      {
        title: 'Professional Templates',
        description: 'Ready-to-use CV templates, networking email scripts, and interview frameworks based on what actually works.',
      },
      {
        title: '30-Day Action Plan',
        description: 'Step-by-step 30-day plan to get started immediately with clear daily actions and milestones.',
      },
    ],
    isFoundingPriceActive: true,
    downloadFiles: [
      {
        name: 'The Unhinged Guide to Breaking Into Tech',
        description: 'The complete guide (PDF)',
        filename: 'The-Unhinged-Guide-to-Breaking-Into-Tech.pdf',
      },
      {
        name: 'The Unhinged Toolkit - Printable',
        description: 'Printable version for handwritten notes (PDF)',
        filename: 'The-Unhinged-Toolkit-Printable-Worksheets.pdf',
      },
      {
        name: 'The Unhinged Toolkit - Fillable',
        description: 'Fillable version to type answers and save (PDF)',
        filename: 'The-Unhinged-Toolkit-Fillable.pdf',
      },
    ],
  },
];

export const getProductBySlug = (slug: string): Product | undefined => {
  return products.find((product) => product.slug === slug);
};

export const getActiveProducts = (): Product[] => {
  return products.filter((product) => product.status === 'active');
};
